'use client'

import { useEffect } from 'react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { Logo } from '@/components/ui/logo'
import { Button } from '@/components/ui/button'
import { Home, RefreshCw, AlertTriangle, LogIn } from 'lucide-react'
import { reportError } from '@/hooks/use-error-reporter'
import { useTranslations } from 'next-intl'

export default function AuthError({
  error,
  reset,
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  const pathname = usePathname()
  const t = useTranslations('errors.auth')

  // Determine which auth page the error occurred on
  const getAuthContext = () => {
    if (pathname?.includes('/signup')) return 'signup'
    if (pathname?.includes('/login')) return 'login'
    if (pathname?.includes('/reset-password')) return 'resetPassword'
    if (pathname?.includes('/onboarding')) return 'onboarding'
    return 'default'
  }

  const authContext = getAuthContext()

  useEffect(() => {
    // Log the error with auth-specific context
    console.error(`[Auth Error] ${authContext} page error:`, error)

    reportError(error, {
      digest: error.digest,
      metadata: {
        type: 'error_boundary',
        boundary: 'auth',
        authContext,
        pathname,
        // Include any additional context that might help debugging
        errorName: error.name,
        errorConstructor: error.constructor.name,
      },
    })
  }, [error, authContext, pathname])

  return (
    <div className="min-h-screen bg-gradient-to-br from-zinc-900 via-zinc-800 to-zinc-900 flex flex-col items-center justify-center p-4">
      <div className="text-center max-w-md">
        {/* Logo */}
        <div className="mb-8">
          <Logo size="lg" />
        </div>

        {/* Error Icon */}
        <div className="mb-6">
          <div className="p-4 rounded-full bg-red-500/10 inline-block">
            <AlertTriangle className="h-12 w-12 text-red-400" />
          </div>
        </div>

        {/* Error Message */}
        <h1 className="text-3xl font-bold text-white mb-2">{t('title')}</h1>
        <p className="text-zinc-400 mb-6">
          {t(authContext)} {t('teamNotified')}
        </p>

        {/* Error Details (in development) */}
        {process.env.NODE_ENV === 'development' && (
          <div className="mb-6 p-4 rounded-lg bg-red-500/10 border border-red-500/20 text-left">
            <p className="text-sm font-mono text-red-400 break-all">
              {error.message}
            </p>
            {error.digest && (
              <p className="text-xs text-zinc-500 mt-2">
                {t('errorId', { digest: error.digest })}
              </p>
            )}
            {error.stack && (
              <pre className="text-xs text-zinc-500 mt-2 overflow-auto max-h-40">
                {error.stack}
              </pre>
            )}
          </div>
        )}

        {/* Production error reference */}
        {process.env.NODE_ENV === 'production' && error.digest && (
          <p className="text-xs text-zinc-600 mb-4">
            {t('reference', { digest: error.digest })}
          </p>
        )}

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3 justify-center mb-8">
          <Button
            onClick={reset}
            variant="outline"
            className="border-zinc-700"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            {t('tryAgain')}
          </Button>
          {authContext === 'signup' ? (
            <Link href="/login">
              <Button className="bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700 w-full sm:w-auto">
                <LogIn className="h-4 w-4 mr-2" />
                {t('goToLogin')}
              </Button>
            </Link>
          ) : authContext === 'login' ? (
            <Link href="/signup">
              <Button className="bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700 w-full sm:w-auto">
                <LogIn className="h-4 w-4 mr-2" />
                {t('createAccount')}
              </Button>
            </Link>
          ) : (
            <Link href="/">
              <Button className="bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700 w-full sm:w-auto">
                <Home className="h-4 w-4 mr-2" />
                {t('goHome')}
              </Button>
            </Link>
          )}
        </div>

        {/* Support */}
        <p className="text-sm text-zinc-500">
          {t.rich('contactSupport', {
            link: (chunks: React.ReactNode) => (
              <a href="mailto:support@iperform.live" className="text-violet-400 hover:text-violet-300">
                {chunks}
              </a>
            ),
          })}
        </p>
      </div>
    </div>
  )
}
